import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Dialog, TextField } from '@material-ui/core';
import { DriverAuthContext, navigate } from '../DriverApp';
import DriverRouteMap from '../components/DriverRouteMap';

const ABORT_TYPES = [
  { key: 'breakdown', label: '🔧 Avaria mecânica' },
  { key: 'accident',  label: '💥 Acidente' },
  { key: 'illness',   label: '🤒 Doença / Indisposição' },
  { key: 'other',     label: '❗ Outro' },
];

export default function DriverRouteDetail({ routeId }) {
  const { token, api } = useContext(DriverAuthContext);
  const [route, setRoute] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [abortDialog, setAbortDialog] = useState(null); // { type, reason }

  async function load() {
    try {
      const res = await fetch(`${api}/mobile/my-routes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const found = Array.isArray(data) ? data.find(r => String(r.id) === String(routeId)) : null;
      setRoute(found || null);
    } catch (e) { setError(e.message); }
  }

  useEffect(() => { load(); }, [routeId]); // eslint-disable-line

  async function changeStatus(status) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/mobile/routes/${routeId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Falha');
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function submitAbort() {
    if (!abortDialog?.type || !abortDialog?.reason?.trim()) {
      setError('Indica o tipo e descreve o problema.');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${api}/mobile/routes/${routeId}/abort`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: abortDialog.type,
          reason: abortDialog.reason.trim(),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Falha a reportar avaria');
      }
      setAbortDialog(null);
      // Driver is now unavailable — bounce back to routes list. The list
      // will be empty (the route is aborted) and the operator takes over.
      navigate('/driver/routes');
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (!route && !error) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!route) {
    return (
      <Box style={{ padding: 20, textAlign: 'center' }}>
        <Typography>Rota não encontrada.</Typography>
        <Button onClick={() => navigate('/driver/routes')}>Voltar</Button>
      </Box>
    );
  }

  const orders = route.transportOrders || [];

  return (
    <Box style={{ minHeight: '100vh', background: '#f1f5f9', paddingBottom: 30 }}>
      <Box style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
        padding: '14px 16px', boxShadow: '0 2px 8px rgba(37,99,235,0.2)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <IconButton onClick={() => navigate('/driver/routes')} style={{ color: '#fff', padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M15.41 16.59 10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
        </IconButton>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontSize: '0.98rem', fontWeight: 800 }}>
            {route.name || `Rota #${route.id}`}
          </Typography>
          <Typography style={{ fontSize: '0.7rem', opacity: 0.9 }}>
            {orders.length} {orders.length === 1 ? 'ordem' : 'ordens'} · {route.status}
          </Typography>
        </Box>
      </Box>

      {/* Route map — driver sees the whole route polyline + stop markers at a glance */}
      <DriverRouteMap route={route} height={240} />

      {/* Action bar */}
      <Box style={{ padding: '12px 14px' }}>
        {route.status === 'planned' && (
          <Button
            fullWidth variant="contained" disabled={busy}
            onClick={() => changeStatus('in_progress')}
            style={{
              padding: '12px', borderRadius: 10, textTransform: 'none', fontSize: '0.9rem', fontWeight: 700,
              background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff',
            }}>
            ▶ Iniciar rota
          </Button>
        )}
        {route.status === 'in_progress' && (() => {
          // Per-OT delivery model: the route auto-completes when the LAST OT
          // is reported as resolved (backend-side). The driver no longer
          // closes the route manually — we just show the progress and prompt
          // them to report the next pending service.
          const total = orders.length;
          const done = orders.filter(o => !!o.assistanceReport).length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const next = orders.find(o => !o.assistanceReport);
          return (
            <Box style={{
              padding: '12px 14px', borderRadius: 12,
              background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
              border: '1px solid #93c5fd',
            }}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e40af' }}>
                  Progresso da rota
                </Typography>
                <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#1e3a8a' }}>
                  {done}/{total} ({pct}%)
                </Typography>
              </Box>
              <Box style={{ height: 8, background: '#dbeafe', borderRadius: 4, overflow: 'hidden' }}>
                <Box style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)', transition: 'width 0.25s' }} />
              </Box>
              {next ? (
                <Box
                  onClick={() => navigate(`/driver/orders/${next.id}`)}
                  style={{
                    marginTop: 10, padding: '8px 12px', borderRadius: 8,
                    background: '#fff', border: '1px solid #93c5fd',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <Box style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: '#3b82f6', color: '#fff',
                    fontSize: '0.78rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>▶</Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                      Próximo serviço
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {next.orderNumber} · {next.deliveryName || next.deliveryAddress || ''}
                    </Typography>
                  </Box>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#3b82f6">
                    <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                  </svg>
                </Box>
              ) : (
                <Box style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#dcfce7', border: '1px solid #86efac', textAlign: 'center' }}>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: '#166534' }}>
                    ✓ Todos os serviços concluídos
                  </Typography>
                  <Typography style={{ fontSize: '0.66rem', color: '#15803d', marginTop: 2 }}>
                    A rota foi marcada como concluída.
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>

      {/* Orders list */}
      <Box style={{ padding: '0 14px' }}>
        <Typography style={{ fontSize: '0.74rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Ordens de serviço ({orders.length})
        </Typography>
        {orders.map((o, idx) => {
          const isAssistance = o.serviceType === 'assistance';
          const hasReport = !!o.assistanceReport;
          return (
            <Box key={o.id}
              onClick={() => navigate(`/driver/orders/${o.id}`)}
              style={{
                background: '#fff', borderRadius: 12, padding: 12, marginBottom: 8,
                border: '1px solid #e2e8f0', cursor: 'pointer',
              }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Box style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: hasReport ? '#dcfce7' : '#dbeafe',
                  color: hasReport ? '#166534' : '#1e40af',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.78rem', fontWeight: 800, flexShrink: 0,
                }}>
                  {hasReport ? '✓' : idx + 1}
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>
                      {o.orderNumber || `OT-${o.id}`}
                    </Typography>
                    {isAssistance && (
                      <Box style={{ padding: '1px 6px', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '0.56rem', fontWeight: 700 }}>
                        🔧 Assistência
                      </Box>
                    )}
                    {o.slaHours != null && (
                      <Box style={{ padding: '1px 6px', borderRadius: 8, background: '#fef3c7', color: '#92400e', fontSize: '0.56rem', fontWeight: 700 }}>
                        ⏱ {o.slaHours}h
                      </Box>
                    )}
                  </Box>
                  <Typography style={{ fontSize: '0.7rem', color: '#334155', marginTop: 2 }}>
                    {isAssistance ? '🔧 Local: ' : '📍 Destino: '}{o.deliveryName || o.deliveryAddress || '—'}
                  </Typography>
                  {o.unloadingTime && (
                    <Typography style={{ fontSize: '0.64rem', color: '#64748b', marginTop: 1 }}>
                      Tempo estimado: {o.unloadingTime} min
                    </Typography>
                  )}
                </Box>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#94a3b8">
                  <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Emergency: report a breakdown / accident. Only meaningful while
          the route is in progress. Pressing this releases all unfinished
          OTs back to dispatch and marks the driver unavailable. */}
      {(route.status === 'in_progress' || route.status === 'planned') && (
        <Box style={{ padding: '20px 14px 8px' }}>
          <Button
            fullWidth variant="outlined"
            onClick={() => setAbortDialog({ type: '', reason: '' })}
            style={{
              padding: '10px', borderRadius: 10, textTransform: 'none',
              fontSize: '0.86rem', fontWeight: 700,
              border: '2px solid #dc2626', color: '#dc2626', background: '#fee2e2',
            }}>
            🚨 Reportar avaria / incidente
          </Button>
        </Box>
      )}

      <Dialog open={!!abortDialog} onClose={() => !busy && setAbortDialog(null)} fullWidth>
        {abortDialog && (
          <Box style={{ padding: 20 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 800, color: '#991b1b', marginBottom: 4 }}>
              🚨 Reportar avaria / incidente
            </Typography>
            <Typography style={{ fontSize: '0.74rem', color: '#64748b', marginBottom: 14 }}>
              Ao confirmar, todas as ordens não entregues voltam a ficar disponíveis para o operador atribuir a outro motorista, e ficas marcado como indisponível até que o operador autorize o regresso.
            </Typography>

            <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              Tipo de incidente
            </Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {ABORT_TYPES.map(t => {
                const active = abortDialog.type === t.key;
                return (
                  <Box key={t.key}
                    onClick={() => setAbortDialog(d => ({ ...d, type: t.key }))}
                    style={{
                      padding: '6px 12px', borderRadius: 18, cursor: 'pointer',
                      fontSize: '0.74rem', fontWeight: 700,
                      background: active ? '#dc2626' : '#fff',
                      color: active ? '#fff' : '#dc2626',
                      border: `1.5px solid ${active ? '#dc2626' : '#fecaca'}`,
                    }}>
                    {t.label}
                  </Box>
                );
              })}
            </Box>

            <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              Descrição do problema
            </Typography>
            <TextField
              multiline rows={3} fullWidth variant="outlined" size="small"
              placeholder="Ex: bomba de combustível avariada, peça parada na A1 km 220"
              value={abortDialog.reason}
              onChange={(e) => setAbortDialog(d => ({ ...d, reason: e.target.value }))}
              inputProps={{ style: { fontSize: '0.82rem' } }}
            />

            {error && (
              <Box style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: '0.74rem', marginTop: 10 }}>
                {error}
              </Box>
            )}

            <Box style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <Button onClick={() => setAbortDialog(null)} disabled={busy}
                style={{ flex: 1, padding: 10, textTransform: 'none', fontSize: '0.78rem', color: '#64748b' }}>
                Cancelar
              </Button>
              <Button
                onClick={submitAbort} disabled={busy || !abortDialog.type || !abortDialog.reason?.trim()}
                style={{
                  flex: 2, padding: 10, textTransform: 'none', fontSize: '0.82rem', fontWeight: 700,
                  background: busy ? '#cbd5e1' : 'linear-gradient(135deg, #dc2626, #991b1b)', color: '#fff',
                }}>
                {busy ? 'A enviar...' : '🚨 Confirmar avaria'}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
